/**
 * Keeps the Figma-positioned source nav in the document and presents a fixed
 * mirror only while that source nav is out of view. This avoids moving a
 * single header between two visual positions on the way back to the top.
 *
 * Re-init safe: a Theme Editor `shopify:section:load` replaces the header
 * markup wholesale, which would otherwise leave a stale clone (old CTA
 * label) mirroring a detached source while the new header stays unbound.
 * `initHeaderScroll` tears the previous clone down and rebinds to the fresh
 * node; the single scroll listener below is added once and always reads the
 * current module-level references, so reloads cannot leak listeners.
 */
/** @type {HTMLElement | null} */
let sourceHeader = null;
/** @type {HTMLElement | null} */
let stickyHeader = null;
let stickyVisible = false;
/** @type {number | undefined} */
let frame;

/** @param {boolean} visible */
function setStickyVisible(visible) {
  if (!sourceHeader || !stickyHeader || visible === stickyVisible) return;
  stickyVisible = visible;

  if (visible) {
    stickyHeader.removeAttribute('inert');
    stickyHeader.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(() => stickyHeader?.classList.add('is-visible'));
  } else {
    stickyHeader.classList.remove('is-visible');
    stickyHeader.setAttribute('inert', '');
    stickyHeader.setAttribute('aria-hidden', 'true');
  }

  sourceHeader.toggleAttribute('inert', visible);
}

function updateHeaderState() {
  frame = undefined;
  if (!sourceHeader || !stickyHeader) return;

  const sourceBounds = sourceHeader.getBoundingClientRect();
  const stickyHeight = stickyHeader.getBoundingClientRect().height;
  const shouldShowSticky = stickyVisible
    ? sourceBounds.bottom < stickyHeight
    : sourceBounds.bottom <= 0;

  setStickyVisible(shouldShowSticky);
}

function requestUpdate() {
  if (!frame) frame = window.requestAnimationFrame(updateHeaderState);
}

/** Removes the previous clone/listeners before rebinding to a fresh header. */
function teardown() {
  stickyHeader?.remove();
  sourceHeader?.removeAttribute('inert');
  sourceHeader = null;
  stickyHeader = null;
  stickyVisible = false;
  if (frame) {
    window.cancelAnimationFrame(frame);
    frame = undefined;
  }
}

/** @param {Document | Element} root */
function initHeaderScroll(root = document) {
  const nextSource = /** @type {HTMLElement | null} */ (root.querySelector('.site-header:not(.site-header--sticky-clone)'));
  if (!nextSource || nextSource === sourceHeader) return;

  teardown();

  sourceHeader = nextSource;
  stickyHeader = /** @type {HTMLElement} */ (sourceHeader.cloneNode(true));
  stickyHeader.classList.add('site-header--sticky-clone');
  stickyHeader.setAttribute('aria-hidden', 'true');
  stickyHeader.setAttribute('inert', '');
  sourceHeader.after(stickyHeader);

  updateHeaderState();
}

initHeaderScroll();

// Added once, ever: reads the current sourceHeader/stickyHeader references
// rather than closing over a specific pair, so a section reload cannot leak
// a second listener.
window.addEventListener('scroll', requestUpdate, { passive: true });

document.addEventListener('shopify:section:load', (event) => {
  initHeaderScroll(/** @type {Element} */ (event.target));
});
